/**
 * Formulário da Roda da Vida — pontuação (0-10) + observação por área.
 */
import { useEffect, useMemo, useState } from "react";
import { Save, Loader2, Info } from "lucide-react";
import { myApi } from "../../api";

const NAVY = "#0F1829";
const GREEN = "#4A5E2E";

export default function RodaForm({ areas = [], initial = null, onSaved, onError }) {
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Hidrata com valores iniciais (avaliação em edição) ou defaults.
    const s = {}, n = {};
    for (const a of areas) {
      s[a.key] = initial?.scores?.[a.key] ?? 5;
      n[a.key] = initial?.notes?.[a.key] ?? "";
    }
    setScores(s);
    setNotes(n);
  }, [initial, areas.length]);

  const total = useMemo(() => {
    const vals = Object.values(scores).map((v) => Number(v) || 0);
    if (!vals.length) return 0;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }, [scores]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { scores, notes };
      if (initial?.id) payload.id = initial.id;
      const doc = await myApi.coachRodaSave(payload);
      onSaved(doc);
    } catch (e) {
      onError(e.message || "Falha ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (!areas.length) {
    return (
      <div className="text-sm text-[#6B7280]" data-testid="coach-form-loading">
        A preparar o formulário…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="coach-roda-form">
      <div className="bg-[#FBFAF7] rounded-xl border border-[#E5E7EB] p-4 flex items-start gap-3">
        <Info size={18} className="text-[#4A5E2E] mt-0.5 shrink-0" />
        <p className="text-sm text-[#374151] leading-relaxed">
          Avalia como te sentes em cada área <b>hoje</b> (0 = muito insatisfeito,
          10 = plenamente realizado) e escreve o que quiseres partilhar. Quanto
          mais honesto fores, mais valiosa será a análise do coach.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {areas.map((a) => (
          <AreaCard
            key={a.key}
            area={a}
            score={scores[a.key] ?? 5}
            note={notes[a.key] ?? ""}
            onScore={(v) => setScores((s) => ({ ...s, [a.key]: v }))}
            onNote={(v) => setNotes((n) => ({ ...n, [a.key]: v }))}
          />
        ))}
      </div>

      <div className="sticky bottom-2 z-10 bg-white/95 backdrop-blur rounded-xl border border-[#E5E7EB] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-lg">
        <div className="text-sm text-[#374151] text-center sm:text-left">
          Média global:{" "}
          <span className="font-semibold text-[#0F1829]" data-testid="coach-avg">
            {total}/10
          </span>
        </div>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="coach-btn-save"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0F1829] text-white text-sm font-medium px-5 py-2.5 rounded-md hover:opacity-90 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {initial?.id ? "Actualizar avaliação" : "Guardar & Analisar"}
        </button>
      </div>
    </div>
  );
}

function AreaCard({ area, score, note, onScore, onNote }) {
  return (
    <div
      className="bg-white rounded-xl border border-[#E5E7EB] p-4 hover:border-[#4A5E2E]/40 transition"
      data-testid={`coach-area-${area.key.replace(/\s/g, "-")}`}
    >
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-semibold text-[#0F1829]">{area.label}</h3>
        <span
          className="text-sm font-mono font-bold"
          style={{ color: scoreColor(score) }}
          data-testid={`coach-area-score-${area.key.replace(/\s/g, "-")}`}
        >
          {score}/10
        </span>
      </div>
      {area.hint && <p className="text-xs text-[#6B7280] mb-3">{area.hint}</p>}
      <input
        type="range"
        min={0}
        max={10}
        step={1}
        value={score}
        onChange={(e) => onScore(Number(e.target.value))}
        className="w-full accent-[#4A5E2E] mb-3"
        aria-label={`Pontuação ${area.label}`}
      />
      <textarea
        placeholder="O que sentes / o que gostavas que fosse diferente? (opcional)"
        value={note}
        onChange={(e) => onNote(e.target.value)}
        maxLength={2000}
        rows={3}
        className="w-full text-sm border border-[#E5E7EB] rounded-md p-2 focus:ring-2 focus:ring-[#4A5E2E]/30 focus:border-[#4A5E2E] outline-none resize-y"
        data-testid={`coach-area-note-${area.key.replace(/\s/g, "-")}`}
      />
    </div>
  );
}

function scoreColor(v) {
  if (v <= 3) return "#B91C1C";
  if (v <= 6) return "#C2831A";
  return "#4A5E2E";
}
